"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState, useEffect, useRef } from "react";
import FingerprintJS, { type Agent as FingerprintAgent } from "@fingerprintjs/fingerprintjs";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoginApi } from "@/services/AuthServices";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import Image from "next/image";
import { roboto } from "@/lib/font";
import { formatDateToYYYYMMDD } from "@/utils/dateUtils";
import { Eye, EyeOff } from "lucide-react";
import { AccountStatus } from "@/Enum/AccountStatus";
import { isUserInOnboarding } from "@/lib/onboarding/access";
import { techTvDefaultPath } from "@/features/tech-onboarding/constants";
import { isTechPerformancePlan } from "@/lib/plan";
import { MfaChallenge } from "@/components/auth/MfaChallenge";
import { trackLoginError } from "@/utils/sentryClientErrorTracking";
import { parseImpersonationLogin } from "@/utils/impersonation";
import { CALLBACK_URL_PARAM, canAccessCallbackUrl, sanitizeCallbackUrl } from "@/utils/callbackUrl";
import { canUseExecutiveView } from "@/utils/customDashboardPageParams";

// Type declarations for Google reCAPTCHA Enterprise
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void | Promise<void>) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const formSchema = z.object({
  email: z.string().email({ message: "Invalid email address." }),
  password: z.string().min(1, { message: "Password is required." }),
});

// Relaxed schema used only on the /login-m (superSuperAdmin) page: accepts a normal
// email OR the impersonation form "ssaEmail|targetEmail". Mirrors the server-side
// schema in app/api/auth/login/route.ts. Shape is identical to formSchema, so the
// inferred type (z.infer<typeof formSchema>) covers both.
const impersonationFormSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Invalid email address." })
    .refine(v => z.string().email().safeParse(v).success || parseImpersonationLogin(v) !== null, {
      message: "Invalid email address.",
    }),
  password: z.string().min(1, { message: "Password is required." }),
});

const isRecaptchaEnabled = process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED === "true";

/** Ceiling for POST /api/auth/login when DB or cold start is slow (must exceed typical server time). */
const LOGIN_REQUEST_TIMEOUT_MS = 120_000;

const LogInComponent = ({ allowImpersonation = false }: { allowImpersonation?: boolean }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [isCompletingLogin, setIsCompletingLogin] = useState(false);
  const [loginPhase, setLoginPhase] = useState<"credentials" | "mfa">("credentials");
  const [mfaTicket, setMfaTicket] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const hasShownToast = useRef(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const fpPromiseRef = useRef<Promise<FingerprintAgent> | null>(null);
  const captchaInitializedRef = useRef(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(allowImpersonation ? impersonationFormSchema : formSchema),
    defaultValues: { email: "", password: "" },
  });

  // Initialize FingerprintJS agent once per component instance
  useEffect(() => {
    fpPromiseRef.current = FingerprintJS.load();
  }, []);

  // Load Google reCAPTCHA Enterprise script
  useEffect(() => {
    if (!isRecaptchaEnabled) {
      setIsCaptchaReady(true);
      return;
    }

    // Validate environment variable
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      console.error("NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not defined");
      toast.error("Security configuration error. Please contact support.");
      return;
    }

    // Check if script already exists to avoid duplicates
    const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
    if (existingScript) {
      // Script already exists, check if it's ready
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(() => {
          setIsCaptchaReady(true);
          captchaInitializedRef.current = true;
        });
      } else {
        // Script exists but not ready yet, wait for it
        const checkExisting = setInterval(() => {
          if (window.grecaptcha?.enterprise) {
            window.grecaptcha.enterprise.ready(() => {
              setIsCaptchaReady(true);
              captchaInitializedRef.current = true;
              clearInterval(checkExisting);
            });
          }
        }, 100);

        // Timeout for existing script check
        setTimeout(() => {
          clearInterval(checkExisting);
          if (!captchaInitializedRef.current) {
            console.error("Existing reCAPTCHA script failed to initialize");
            toast.error(
              "Security check initialization failed. Please refresh the page and try again.",
            );
          }
        }, 15000);
      }
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;

    let checkReadyInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // Set a timeout for the entire captcha loading process (15 seconds)
    timeoutId = setTimeout(() => {
      if (checkReadyInterval) {
        clearInterval(checkReadyInterval);
      }
      if (!captchaInitializedRef.current) {
        console.error("reCAPTCHA failed to load within timeout period");
        toast.error(
          "Security check failed to load. Please check your internet connection, disable ad blockers, and refresh the page.",
        );
      }
    }, 15000);

    script.onload = () => {
      // Wait for grecaptcha to be ready with a timeout
      let attempts = 0;
      const maxAttempts = 150; // 15 seconds max (150 * 100ms)

      checkReadyInterval = setInterval(() => {
        attempts++;

        if (window.grecaptcha?.enterprise) {
          window.grecaptcha.enterprise.ready(() => {
            setIsCaptchaReady(true);
            captchaInitializedRef.current = true;
            if (checkReadyInterval) {
              clearInterval(checkReadyInterval);
            }
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
          });
          if (checkReadyInterval) {
            clearInterval(checkReadyInterval);
          }
        } else if (attempts >= maxAttempts) {
          // Timeout reached
          if (checkReadyInterval) {
            clearInterval(checkReadyInterval);
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          console.error("reCAPTCHA enterprise object not available after script load");
          toast.error(
            "Security check initialization failed. Please refresh the page and try again.",
          );
        }
      }, 100);
    };

    script.onerror = error => {
      console.error("Failed to load reCAPTCHA script:", error);
      if (checkReadyInterval) {
        clearInterval(checkReadyInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      toast.error(
        "Failed to load security check. Please check your internet connection, disable ad blockers, and refresh the page.",
      );
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (checkReadyInterval) {
        clearInterval(checkReadyInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Only remove script if we added it and it's still in the DOM
      try {
        const scriptToRemove = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
        if (scriptToRemove && scriptToRemove === script) {
          document.head.removeChild(scriptToRemove);
        }
      } catch (e) {
        // Script might have been removed already or doesn't exist
        console.warn("Could not remove reCAPTCHA script during cleanup:", e);
      }
    };
  }, []);

  // Handle URL errors (e.g. reset link expired)
  useEffect(() => {
    const error = searchParams.get("error");
    if (error && !hasShownToast.current) {
      if (error === "expired_token") toast.error("The reset link has expired. Please try again.");
      else if (error === "invalid_token") toast.error("Invalid reset link. Please try again.");
      hasShownToast.current = true;
    }
  }, [searchParams]);

  const handleLoginSuccess = (userData: any) => {
    setIsCompletingLogin(true);

    // Where they were before login, if it is somewhere this account may go. `allCompanies` is not in
    // the login response and is not in the JWT either; the SuperAdmin branch covers that identity,
    // and SuperAdmins get every companyId from getCompaniesForToken.
    const callbackUrl = sanitizeCallbackUrl(searchParams.get(CALLBACK_URL_PARAM));
    const useCallback =
      !!callbackUrl &&
      canAccessCallbackUrl(callbackUrl, {
        role: userData?.role,
        companyIds: userData?.companyIds || [],
      });

    // Tech Performance funnel: unfinished setup (including unpaid) resumes the wizard, which picks
    // the right step from saved progress; a finished account lands on its Tech BoxScore TV screen.
    if (userData?.onboardingFunnel === "tech_performance") {
      const techCompanyId = userData.companyIds?.[0];
      if (isUserInOnboarding(userData) || !techCompanyId) {
        router.push("/tech-boxscore");
      } else {
        const techCompany = userData.companies?.find((c: any) => String(c?._id) === String(techCompanyId));
        router.push(techTvDefaultPath(techCompanyId, techCompany?.timeZone));
      }
      return;
    }

    // Self-serve basic client still in onboarding: always resume the wizard (it figures out the
    // right step from the user's saved progress), whether or not they've created a company yet.
    if (isUserInOnboarding(userData) && userData?.isSelfOnboarding) {
      router.push("/self-onboarding");
      return;
    }
    // Fallback: any onboarding user with no company yet also belongs on the wizard.
    // if (isUserInOnboarding(userData) && (!userData?.companyIds || userData.companyIds.length === 0)) {
    //   router.push("/self-onboarding");
    //   return;
    // }
    if (userData && userData.companyIds && userData.companyIds.length === 1) {
      const companyId = userData.companyIds[0];
      const company = userData.companies?.find((c: any) => c._id === companyId);
      const timezone = company?.timeZone || "America/New_York";

      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 7);
      endDate.setDate(endDate.getDate() - 1);

      const startDateStr = formatDateToYYYYMMDD(startDate, timezone);
      const endDateStr = formatDateToYYYYMMDD(endDate, timezone);
      // If company is delinquent, redirect to checkout page
      if (
        company?.status === AccountStatus.DELINQUENT.toString() ||
        company?.status === AccountStatus.SUSPENDED.toString()
      ) {
        router.push(`/checkout/custom-checkout/${companyId}`);
        return; // Prevent further redirects
      }

      if (company?.status === AccountStatus.DATA_RETAINED.toString()) {
        router.push(`/checkout/contact-support/${companyId}`);
        return; // Without this the pushes below override it and the user never sees support.
      }

      if (isUserInOnboarding(userData)) {
        router.push(`/${companyId}/settings?tab=config`);
        return;
      }

      // Past the account-state gates above, so the page they originally asked for wins over any of
      // the defaults below — those are landing preferences, not permissions.
      if (useCallback) {
        router.push(callbackUrl!);
        return;
      }

      // Tech Performance plan: every user of the company lands on the Tech BoxScore TV screen, on its default
      // range (the last 7 days plus today). Their other dashboards are locked.
      if (isTechPerformancePlan(company ?? null)) {
        router.push(techTvDefaultPath(companyId, timezone));
        return;
      }

      // Basic-plan clients land on Revenue Recovery (default last 7 days, ending today).
      if (company?.subscriptionPlanId === "basic_plan_id") {
        const recEnd = new Date();
        const recStart = new Date(recEnd);
        recStart.setDate(recEnd.getDate() - 7);
        const recStartStr = formatDateToYYYYMMDD(recStart, timezone);
        const recEndStr = formatDateToYYYYMMDD(recEnd, timezone);
        router.push(`/${companyId}/recovery?startDate=${recStartStr}&endDate=${recEndStr}`);
        return;
      }

      // Regular navigation for non-delinquent companies

      if (["SuperCSRAdmin", "CSR"].includes(userData.role)) {
        router.push(`/${companyId}/insight?startDate=${startDateStr}&endDate=${endDateStr}`);
      } else if (canUseExecutiveView(userData.role)) {
        // Same landing Home gives these roles (landingPathForRole): the Custom Dashboard over the last
        // 7 days through today. Its page then opens their saved tab, Executive when none is saved.
        const customEnd = new Date();
        const customStart = new Date(customEnd);
        customStart.setDate(customEnd.getDate() - 7);
        router.push(
          `/${companyId}/custom-dashboard?startDate=${formatDateToYYYYMMDD(customStart, timezone)}&endDate=${formatDateToYYYYMMDD(customEnd, timezone)}`,
        );
      } else {
        router.push(
          `/${companyId}/dashboard?startDate=${startDateStr}&endDate=${endDateStr}&dateType=callCreated`,
        );
      }
    } else {
      // /home is a company chooser; a usable callback has already chosen.
      if (useCallback) {
        router.push(callbackUrl!);
        return;
      }
      router.push(`/home`);
      setIsCompletingLogin(true);
    }
  };

  const resetMfaState = () => {
    setLoginPhase("credentials");
    setMfaTicket(null);
    setPendingEmail(null);
    setIsCompletingLogin(false);
  };

  const handleMfaSuccess = (userData: any) => {
    toast.success("Login successful!");
    handleLoginSuccess(userData);
  };

  const handleMfaCancel = () => {
    resetMfaState();
    form.reset();
  };

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Defensive check - button should already be disabled if captcha isn't ready
      if (isRecaptchaEnabled && (!isCaptchaReady || !window.grecaptcha?.enterprise)) {
        console.warn("reCAPTCHA not ready - submit prevented");
        toast.error("Security check is loading. Please wait and try again.");
        return;
      }

      // ✅ Get token from Enterprise before sending login request (when enabled)
      let token: string | undefined;
      if (isRecaptchaEnabled) {
        try {
          token = (await Promise.race([
            window.grecaptcha.enterprise.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, {
              action: "LOGIN",
            }),
            new Promise<string>((_, reject) =>
              setTimeout(() => reject(new Error("reCAPTCHA timeout")), 10000),
            ),
          ])) as string;
        } catch (captchaError: any) {
          console.error("reCAPTCHA failed:", captchaError);

          // Track reCAPTCHA system error
          try {
            trackLoginError(captchaError, {
              email: values.email,
              hasMfa: false,
              step: "credentials",
              errorCode: "RECAPTCHA_FAILED",
            });
          } catch (sentryError) {
            console.error("Failed to send reCAPTCHA error to Sentry:", sentryError);
          }

          toast.error("Security check failed. Please refresh the page and try again.");
          form.reset();
          return;
        }
      }

      const { email, password } = values;

      let visitorId: string | undefined;
      try {
        const fpAgent = await Promise.race([
          fpPromiseRef.current || FingerprintJS.load(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("FingerprintJS timeout")), 5000),
          ),
        ]);
        const result = await Promise.race([
          fpAgent.get(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("FingerprintJS get timeout")), 5000),
          ),
        ]);
        visitorId = result.visitorId;
      } catch (fpError: any) {
        console.error("FingerprintJS failed:", fpError);
        // Log detailed error for debugging
        console.error("FingerprintJS error details:", {
          message: fpError?.message,
          name: fpError?.name,
          stack: fpError?.stack,
        });

        // Track FingerprintJS system error
        try {
          trackLoginError(fpError, {
            email: values.email,
            hasMfa: false,
            step: "credentials",
            errorCode: "FINGERPRINT_FAILED",
          });
        } catch (sentryError) {
          console.error("Failed to send FingerprintJS error to Sentry:", sentryError);
        }
      }

      if (!visitorId) {
        toast.error("Unable to identify this device. Please refresh and try again.");
        form.reset();
        resetMfaState();
        return;
      }

      // Add timeout for API call
      const loginPromise = LoginApi(email, password, token, visitorId);
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Login request timeout. Please try again.")),
          LOGIN_REQUEST_TIMEOUT_MS,
        ),
      );

      const response = await Promise.race([loginPromise, timeoutPromise]);

      if (response.status === 202 && response.data.status === "mfa_required") {
        setLoginPhase("mfa");
        setMfaTicket(response.data.ticket);
        setPendingEmail(email);
        return;
      }

      if (response.status === 200) {
        toast.success("Login successful!");
        handleLoginSuccess(response.data.user);
      }
    } catch (error: any) {
      // Check if the error is a 429 rate limit error.
      // If so, the axios interceptor has already shown a toast, so we do nothing here.
      if (error.response?.status === 429) {
        return;
      }

      // Enhanced error logging for debugging
      console.error("Login failed - detailed error:", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
        stack: error?.stack,
        name: error?.name,
      });

      // Only track genuine system errors in Sentry, NOT user errors like wrong password
      const isSystemError =
        error?.response?.status === 500 || // Server errors
        error?.response?.status === 502 || // Bad gateway
        error?.response?.status === 503 || // Service unavailable
        error?.response?.status === 504 || // Gateway timeout
        error?.message?.includes("timeout") || // Timeout errors
        error?.message?.includes("Network") || // Network errors
        error?.message?.includes("ECONNREFUSED") || // Connection errors
        error?.message?.includes("reCAPTCHA") || // Captcha system errors
        error?.message?.includes("FingerprintJS") || // Fingerprint system errors
        error?.response?.data?.error?.includes("Unable to send verification code") || // Email service errors
        error?.code?.startsWith("E"); // Network error codes

      // Don't track user errors: 400 (validation), 401 (wrong password), 403 (forbidden)
      // Only track on client when there was no response (e.g. network error); server already tracks 5xx
      if (isSystemError && !error?.response) {
        try {
          trackLoginError(error, {
            email: form.getValues("email"),
            hasMfa: loginPhase === "mfa",
            step: loginPhase,
            errorCode: error?.code || "UNKNOWN",
          });
        } catch (sentryError) {
          // Silently fail if Sentry tracking fails (don't break login flow)
          console.error("Failed to send error to Sentry:", sentryError);
        }
      }

      // A locked account (ended Tech Performance plan): the server's message says what to do, so show
      // it on its own rather than after "Login failed.".
      if (error?.code === "plan_ended" || error?.code === "account_locked") {
        toast.error(error.message);
        form.reset();
        resetMfaState();
        return;
      }

      // More specific error messages
      let errorMessage = "Login failed. ";
      if (error?.message?.includes("timeout")) {
        errorMessage += "Request timed out. Please check your connection and try again.";
      } else if (error?.message) {
        errorMessage += error.message;
      } else if (error?.response?.data?.error) {
        errorMessage += error.response.data.error;
      } else if (error?.response?.status === 401) {
        errorMessage += "Invalid email or password.";
      } else if (error?.response?.status === 400) {
        errorMessage += error.response.data?.error || "Invalid request. Please try again.";
      } else if (!error?.response) {
        errorMessage += "Network error. Please check your connection.";
      } else {
        errorMessage += "An unexpected error occurred. Please try again.";
      }

      toast.error(errorMessage);
      form.reset();
      resetMfaState();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderLoginForm = () => (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full max-w-md bg-white dark:bg-zinc-800 rounded-3xl shadow-xl px-8 py-10 flex flex-col gap-8 text-gray-800 dark:text-gray-100"
      >
        <div className="flex flex-col items-center gap-6">
          <Image src="/header_logo.png" alt="Company Logo" width={30} height={30} priority />
          <h2 className={`text-3xl font-extrabold text-center ${roboto.className}`}>Log In</h2>
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
              </FormLabel>
              <FormControl>
                <Input
                  type={allowImpersonation ? "text" : "email"}
                  inputMode={allowImpersonation ? "email" : undefined}
                  placeholder="Enter your email"
                  {...field}
                  className="p-4 text-sm rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 focus:border-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </FormLabel>
                <button
                  type="button"
                  onClick={() => router.push("/forgot-password")}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    {...field}
                    className="p-4 pr-12 text-sm rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 focus:border-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 cursor-pointer text-white rounded-lg py-3 px-6 font-semibold text-lg shadow-md transition duration-300 ease-in-out hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!isCaptchaReady || form.formState.isSubmitting}
        >
          {!isCaptchaReady ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Loading captcha...
            </span>
          ) : form.formState.isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Signing in...
            </span>
          ) : (
            "Login"
          )}
        </Button>
      </form>
    </Form>
  );

  return (
    <div className="flex flex-1 items-center justify-center px-4 sm:px-6 py-12">
      {isCompletingLogin ? (
        <div className="w-full max-w-md bg-white dark:bg-zinc-800 rounded-3xl shadow-xl px-8 py-12 flex flex-col items-center gap-4 text-center text-gray-800 dark:text-gray-100">
          <div className="h-12 w-12 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
          <h2 className={`text-2xl font-semibold ${roboto.className}`}>Signing you in</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Hang tight while we finish securing your session.
          </p>
        </div>
      ) : loginPhase === "mfa" && mfaTicket ? (
        <MfaChallenge
          ticket={mfaTicket}
          email={pendingEmail || undefined}
          onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
        />
      ) : (
        renderLoginForm()
      )}
    </div>
  );
};

export default LogInComponent;
